import React, { useEffect, useMemo, useRef } from "react";
import { BOARD, SECTIONS } from "../../data/board";
import { connections, stringPath } from "../../data/connections";
import { useCamera } from "./CameraProvider";
import { useBoardGeometry } from "./BoardGeometry";
import { screenToBoard } from "../../utils/mapCamera";

const MINIMAP_W = 232;
const MINIMAP_SCALE = MINIMAP_W / BOARD.width;
const MINIMAP_H = Math.round(BOARD.height * MINIMAP_SCALE);

export const BoardMinimap = () => {
  const { x, y, scale, vw, vh, flyToBoard } = useCamera();
  const { sections, anchors } = useBoardGeometry();
  const viewportRef = useRef(null);
  const rafRef = useRef(0);

  const strings = useMemo(
    () =>
      connections.flatMap((connection, index) => {
        const from = anchors[connection.from];
        const to = anchors[connection.to];
        return from && to ? [{ id: connection.id, type: connection.type, d: stringPath(from, to, index + 1) }] : [];
      }),
    [anchors]
  );

  useEffect(() => {
    const apply = () => {
      rafRef.current = 0;
      const node = viewportRef.current;
      if (!node) return;
      const panX = x.get();
      const panY = y.get();
      const zoom = scale.get();
      const viewW = vw.get();
      const viewH = vh.get();
      const topLeft = screenToBoard(0, 0, panX, panY, zoom);
      const bottomRight = screenToBoard(viewW, viewH, panX, panY, zoom);
      const vx = Math.max(0, topLeft.x);
      const vy = Math.max(0, topLeft.y);
      node.style.left = `${vx * MINIMAP_SCALE}px`;
      node.style.top = `${vy * MINIMAP_SCALE}px`;
      node.style.width = `${Math.max((Math.min(BOARD.width, bottomRight.x) - vx) * MINIMAP_SCALE, 10)}px`;
      node.style.height = `${Math.max((Math.min(BOARD.height, bottomRight.y) - vy) * MINIMAP_SCALE, 8)}px`;
    };

    const schedule = () => {
      if (rafRef.current) return;
      rafRef.current = requestAnimationFrame(apply);
    };

    const unsubX = x.on("change", schedule);
    const unsubY = y.on("change", schedule);
    const unsubS = scale.on("change", schedule);
    const unsubW = vw.on("change", schedule);
    const unsubH = vh.on("change", schedule);
    apply();

    return () => {
      unsubX();
      unsubY();
      unsubS();
      unsubW();
      unsubH();
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [scale, vh, vw, x, y]);

  const jumpTo = (event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const boardX = ((event.clientX - rect.left) / rect.width) * BOARD.width;
    const boardY = ((event.clientY - rect.top) / rect.height) * BOARD.height;
    flyToBoard(boardX, boardY, scale.get(), true);
  };

  return (
    <aside className="board-minimap" aria-label="Board map">
      <button
        type="button"
        className="board-minimap-stage"
        onClick={jumpTo}
        aria-label="Board map. Click to pan."
        style={{ width: MINIMAP_W, height: MINIMAP_H }}
      >
        <svg
          className="board-minimap-schematic"
          viewBox={`0 0 ${BOARD.width} ${BOARD.height}`}
          width={MINIMAP_W}
          height={MINIMAP_H}
          aria-hidden="true"
        >
          {strings.map((path) => (
            <path key={path.id} d={path.d} className={`minimap-string is-${path.type}`} />
          ))}
          {Object.entries(sections).map(([id, rect]) => (
            <rect
              key={id}
              className={`minimap-section${id === "overview" ? " is-subject" : ""}`}
              x={rect.x}
              y={rect.y}
              width={rect.w}
              height={rect.h}
            />
          ))}
        </svg>
        {Object.entries(sections).map(([id, rect]) => (
          <span
            key={id}
            className="minimap-section-label"
            style={{
              left: rect.x * MINIMAP_SCALE,
              top: rect.y * MINIMAP_SCALE,
              width: rect.w * MINIMAP_SCALE,
              height: rect.h * MINIMAP_SCALE,
            }}
          >
            {SECTIONS[id]?.nav}
          </span>
        ))}
        <span ref={viewportRef} className="minimap-viewport" />
      </button>
    </aside>
  );
};

export default BoardMinimap;
